import type { AuthClient } from '@/contexts/auth';
import { CAGE_TYPE_NAMES } from '@/domain/cages/types';
import type { OverviewCage, OverviewLocation } from '@/domain/client';
import { mapCagesByStatus } from '@/domain/client';
import { compareCageNames } from '@/utils/compareCageNames';

import type {
    BreadcrumbCage,
    BreadcrumbLocation,
    CagesByStatus,
    ClientsByStatus,
    LocationsByStatus,
} from './types';

/** The clients whose name holds the filter, by name, archived ones apart. */
export function groupClientsByStatus(clients: AuthClient[], nameFilter: string): ClientsByStatus {
    const matchingClients = clients
        .filter((client) => client.name.toLowerCase().includes(nameFilter.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

    const clientsByStatus: ClientsByStatus = { active: [], inactive: [] };
    for (const client of matchingClients) {
        if (client.archived === true) {
            clientsByStatus.inactive.push(client);
        } else {
            clientsByStatus.active.push(client);
        }
    }
    return clientsByStatus;
}

/** Locations with an active pen first, in the order the overview lists them. */
type GroupedLocation = Pick<OverviewLocation, 'locationId' | 'locationName'> & {
    activeCages: readonly unknown[];
};

export function groupLocationsByStatus(locations: GroupedLocation[]): LocationsByStatus {
    const toEntry = (location: GroupedLocation): BreadcrumbLocation => ({
        id: location.locationId,
        name: location.locationName,
    });
    return locations.reduce(
        (acc: LocationsByStatus, location: GroupedLocation) => {
            if (location.activeCages.length > 0) {
                acc.active.push(toEntry(location));
            } else {
                acc.inactive.push(toEntry(location));
            }
            return acc;
        },
        { active: [], inactive: [] }
    );
}

/** A location's pens in each status bucket, by name. */
export function groupCagesByStatus(location: OverviewLocation): CagesByStatus {
    const toEntry = (cage: OverviewCage): BreadcrumbCage => ({
        id: cage.cageId,
        name: cage.cageName,
        // The pen's *active* cohort, deliberately: for a pen you are not on
        // yet there is no way to know which past cohort you would have meant,
        // and this is the same choice the location table's Open button makes.
        fishGroupId: cage.activeFishGroup?.id,
        fishGroupName: cage.activeFishGroup?.name,
        smolt: String(cage.cageType) === CAGE_TYPE_NAMES.SMOLT_PRODUCTION,
    });
    return mapCagesByStatus(location, (cages) =>
        cages.map(toEntry).sort((a, b) => compareCageNames(a.name, b.name))
    );
}
